import type { Education, Experience } from '@/hooks/use-profile'
import { matchesProfileSkillName } from '@/lib/profile-evidence-display'

export type TopSkillLinkedExperience = {
  title: string
  company: string
  label: string
}

export type TopSkillLinkedEducation = {
  degree: string
  institution: string
  label: string
}

export type TopSkillLinkedEvidence = {
  experiences: TopSkillLinkedExperience[]
  educations: TopSkillLinkedEducation[]
}

function experienceLinksSkill(experience: Experience, skillName: string): boolean {
  return (experience.skills ?? []).some((skill) => matchesProfileSkillName(skillName, skill))
}

function educationLinksSkill(education: Education, skillName: string): boolean {
  return (education.related_skills ?? []).some((skill) => matchesProfileSkillName(skillName, skill))
}

function formatExperienceLabel(experience: Experience): string {
  const title = experience.title.trim()
  const company = experience.company.trim()
  if (title && company) return `${title} at ${company}`
  return title || company || 'Experience'
}

function formatEducationLabel(education: Education): string {
  const degree = education.degree.trim()
  const institution = education.institution.trim()
  if (degree && institution) return `${degree} @ ${institution}`
  return degree || institution || 'Education'
}

/** Resolve profile experience/education rows linked to a demonstrated skill name. */
export function getTopSkillLinkedEvidence(
  skillName: string,
  experiences: Experience[] | undefined,
  education: Education[] | undefined
): TopSkillLinkedEvidence {
  const linkedExperiences = (experiences ?? [])
    .filter((row) => experienceLinksSkill(row, skillName))
    .map((row) => ({
      title: row.title.trim(),
      company: row.company.trim(),
      label: formatExperienceLabel(row),
    }))

  const linkedEducations = (education ?? [])
    .filter((row) => educationLinksSkill(row, skillName))
    .map((row) => ({
      degree: row.degree.trim(),
      institution: row.institution.trim(),
      label: formatEducationLabel(row),
    }))

  return {
    experiences: linkedExperiences,
    educations: linkedEducations,
  }
}
